import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSFinancesData,
  type EnrollmentHYCSFinancesSubmitInput,
  type EnrollmentHYCSFinancesSubmitOutput,
  type EnrollmentHYCSFinancesGetInput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS Finances data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSFinancesData>
 * @example const response = await getEnrollmentHYCSFinancesData()
 */
export const getEnrollmentHYCSFinancesData = async (context?: DefaultContext): Promise<EnrollmentHYCSFinancesData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSFinancesData',
      query: `mutation getEnrollmentHYCSFinancesData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSFinancesData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSFinancesData = createQueryHook<
  EnrollmentHYCSFinancesGetInput,
  EnrollmentHYCSFinancesData
>(getEnrollmentHYCSFinancesData)

/**
 * Submit Enrollment HYCS Finances data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSFinancesSubmitOutput>
 * @example const response = await submitEnrollmentHYCSFinancesData();
 */
export const submitEnrollmentHYCSFinancesData = async (
  enrollmentFinancesSubmitInput: EnrollmentHYCSFinancesSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSFinancesSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSFinancesData',
      query: `mutation submitEnrollmentHYCSFinancesData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentFinancesSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSFinancesSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSFinancesData = createMutationHook<
  EnrollmentHYCSFinancesSubmitInput,
  EnrollmentHYCSFinancesSubmitOutput
>(submitEnrollmentHYCSFinancesData)
